import {
  CheckCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { App, Popover, Space, Typography, theme } from 'antd';
import axios from 'axios';
import { useCallback, type CSSProperties, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { triggerRecompute } from '@/api/endpoints/recompute';
import { useRecomputeProgress } from '@/stores/RecomputeProgressContext';
import type { RecomputeProgress } from '@/types/recompute';

const POPOVER_MIN_WIDTH_PX = 260;
const RUNNING_BG = '#e6f4ff';
const RUNNING_BORDER = '#91caff';
const RUNNING_FG = '#0958d9';
const PENDING_FG = '#d46b08';
const IDLE_FG = '#52c41a';
const HTTP_CONFLICT = 409;
const HTTP_UNAVAILABLE = 503;

export default function RecomputeStatusBadge() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { modal, message } = App.useApp();
  const { progress, refresh } = useRecomputeProgress();

  const running = progress?.status === 'running';
  const pending = progress?.pending ?? 0;
  // Only the "pending, not yet running" state is actionable: clicking it
  // starts the job. Running shows live progress in place; idle has nothing
  // to do.
  const interactive = !running && pending > 0;

  const startRecompute = useCallback(async () => {
    try {
      await triggerRecompute();
      message.success(t('recompute.trigger.started'));
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === HTTP_CONFLICT) {
        message.info(t('recompute.trigger.alreadyRunning'));
      } else if (status === HTTP_UNAVAILABLE) {
        message.error(t('recompute.trigger.unavailable'));
      } else {
        message.error(t('recompute.trigger.error'));
      }
    } finally {
      // Either way, re-poll so the badge reflects the real coordinator state.
      refresh();
    }
  }, [message, t, refresh]);

  const confirmStart = useCallback(() => {
    modal.confirm({
      title: t('recompute.trigger.confirmTitle'),
      content: t('recompute.trigger.confirmContent', { count: pending }),
      okText: t('recompute.trigger.confirmOk'),
      cancelText: t('recompute.trigger.confirmCancel'),
      onOk: startRecompute,
    });
  }, [modal, t, pending, startRecompute]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        confirmStart();
      }
    },
    [confirmStart],
  );

  if (progress === null) {
    return null;
  }

  const pillStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: `2px ${token.paddingSM}px`,
    borderRadius: 999,
    fontSize: 13,
    lineHeight: '22px',
    background: running ? RUNNING_BG : 'transparent',
    border: `1px solid ${running ? RUNNING_BORDER : 'transparent'}`,
    color: running ? RUNNING_FG : pending > 0 ? PENDING_FG : IDLE_FG,
    transition: 'background 160ms ease, border-color 160ms ease',
    cursor: interactive ? 'pointer' : 'default',
    textDecoration: interactive ? 'underline dotted' : 'none',
    textUnderlineOffset: 3,
  };

  return (
    <Popover
      content={<BadgeContent progress={progress} interactive={interactive} />}
      placement="bottomRight"
      trigger="hover"
    >
      <span
        style={pillStyle}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? confirmStart : undefined}
        onKeyDown={interactive ? onKeyDown : undefined}
        aria-label={
          interactive
            ? t('recompute.trigger.hint')
            : running
              ? t('recompute.badge.running', {
                  processed: progress.processed,
                  total: progress.total,
                })
              : t('recompute.badge.idle')
        }
      >
        {running ? (
          <>
            <LoadingOutlined spin style={{ fontSize: 16 }} />
            <Typography.Text strong style={{ color: RUNNING_FG, fontSize: 13 }}>
              {t('recompute.badge.running', {
                processed: progress.processed,
                total: progress.total,
              })}
            </Typography.Text>
          </>
        ) : pending > 0 ? (
          <>
            <WarningOutlined style={{ fontSize: 16 }} />
            <Typography.Text strong style={{ color: PENDING_FG, fontSize: 13 }}>
              {t('recompute.badge.pending', { count: pending })}
            </Typography.Text>
          </>
        ) : (
          <>
            <CheckCircleOutlined style={{ fontSize: 16 }} />
            <Typography.Text style={{ color: IDLE_FG, fontSize: 12 }}>
              {t('recompute.badge.idleShort')}
            </Typography.Text>
          </>
        )}
      </span>
    </Popover>
  );
}

interface BadgeContentProps {
  readonly progress: RecomputeProgress;
  readonly interactive: boolean;
}

function BadgeContent({ progress, interactive }: BadgeContentProps) {
  const { t } = useTranslation();
  return (
    <Space direction="vertical" size="small" style={{ minWidth: POPOVER_MIN_WIDTH_PX }}>
      <Typography.Text strong>{t('recompute.popover.title')}</Typography.Text>
      <Typography.Text>
        {t('recompute.popover.pending', { count: progress.pending })}
      </Typography.Text>
      {progress.status === 'running' && (
        <Typography.Text>
          {t('recompute.popover.progress', {
            processed: progress.processed,
            total: progress.total,
          })}
        </Typography.Text>
      )}
      {progress.failed > 0 && (
        <Typography.Text type="danger">
          {t('recompute.popover.failed', { count: progress.failed })}
        </Typography.Text>
      )}
      {interactive && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('recompute.trigger.hint')}
        </Typography.Text>
      )}
    </Space>
  );
}
